import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocalStorage } from 'shared/hooks/use-local-storage';
import { useQueryParams } from 'shared/hooks/use-query-params';
import { getOffsetLimit } from 'shared/utils/pagination/page-math';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterParamConfig<T = any> = {
  key: string;
  defaultValue: T;
  parser?: (raw: string) => T;
  serializer?: (value: T) => string;
};

type FiltersConfig = Record<string, FilterParamConfig>;

type FilterValues<F extends FiltersConfig> = {
  [K in keyof F]: F[K] extends FilterParamConfig<infer U> ? U : never;
};

/** Minimal shape that any RTK Query hook satisfies. */
type UseQueryHook<TData, TArgs> = (
  args: TArgs,
  options?: { skip?: boolean },
) => {
  data?: TData[];
  isFetching: boolean;
  isError: boolean;
  error?: unknown;
};

export interface IUseOffsetPaginationProps<
  TData,
  TArgs extends Record<string, any>,
  F extends FiltersConfig,
> {
  /** RTK Query useXxxQuery hook */
  useQueryHook: UseQueryHook<TData, TArgs>;
  /** Static args always merged into the query (e.g., { tournamentId }) */
  baseArgs?: Partial<TArgs>;
  /** Map current filter values to query arg fields (excluding limit/offset) */
  mapFiltersToArgs: (filters: FilterValues<F>) => Partial<TArgs>;
  /** Filter param configs for URL sync */
  filtersConfig?: F;
  /** localStorage key for perPage. Default: 'perPage' */
  perPageStorageKey?: string;
  /** Default items per page. Default: 10 */
  defaultPerPage?: number;
  /** Return `{ skip: true }` to prevent the query from running */
  queryOptions?: (args: TArgs) => { skip?: boolean };
}

export interface UseOffsetPaginationResult<TData, F extends FiltersConfig> {
  /** Items for the current page */
  items: TData[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Current page number (1-based, from URL) */
  page: number;
  /** Current items-per-page (from localStorage) */
  limit: number;
  /** Highest page number that has been fetched with data */
  maxLoadedPage: number;
  /** False when the frontier page returned fewer items than the limit */
  hasMorePage: boolean;
  /** maxLoadedPage + (hasMorePage ? 1 : 0) — includes the pseudo page */
  totalPages: number;
  /** Current filter values (from URL) */
  filters: FilterValues<F>;
  setPage: (page: number) => void;
  setFilters: (updates: Partial<FilterValues<F>>) => void;
  setPerPage: (perPage: number) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOffsetPagination<
  TData,
  TArgs extends Record<string, any>,
  F extends FiltersConfig = Record<string, never>,
>(props: IUseOffsetPaginationProps<TData, TArgs, F>): UseOffsetPaginationResult<TData, F> {
  const {
    useQueryHook,
    baseArgs,
    mapFiltersToArgs,
    filtersConfig,
    perPageStorageKey = 'perPage',
    defaultPerPage = 10,
    queryOptions,
  } = props;

  // ---- URL state (page + filters) ----------------------------------------

  const queryParamsConfig = useMemo(() => {
    const cfg: Record<string, any> = {
      page: { key: 'page', defaultValue: 1, parser: Number },
    };
    if (filtersConfig) {
      for (const [name, fc] of Object.entries(filtersConfig)) {
        cfg[name] = fc;
      }
    }
    return cfg;
  }, []); // filtersConfig is expected to be a module-scope constant

  const [params, setParams] = useQueryParams(queryParamsConfig);

  const page: number = (params as any).page ?? 1;

  const filters = useMemo(() => {
    const result: Record<string, any> = {};
    if (filtersConfig) {
      for (const name of Object.keys(filtersConfig)) {
        result[name] = (params as any)[name];
      }
    }
    return result as FilterValues<F>;
  }, [params, filtersConfig]);

  // ---- localStorage (perPage) ---------------------------------------------

  const storageConfig = useMemo(
    () => ({
      perPage: { key: perPageStorageKey, defaultValue: defaultPerPage, parser: Number },
    }),
    [], // storage key is stable
  );

  const [storage, setStorage] = useLocalStorage(storageConfig);
  const perPage: number = storage.perPage;

  // ---- Scope tracking (reset on filter / perPage change) ------------------

  const scopeKey = useMemo(() => JSON.stringify({ filters, perPage }), [filters, perPage]);
  const prevScopeKeyRef = useRef(scopeKey);

  const [paginationState, setPaginationState] = useState({
    maxLoadedPage: 0,
    hasMorePage: true,
  });

  useEffect(() => {
    if (prevScopeKeyRef.current !== scopeKey) {
      prevScopeKeyRef.current = scopeKey;
      setPaginationState({ maxLoadedPage: 0, hasMorePage: true });
      if (page !== 1) {
        setParams({ page: 1 } as any);
      }
    }
  }, [scopeKey, page, setParams]);

  // ---- Build query args & call RTK Query ----------------------------------

  const { offset, limit } = getOffsetLimit(page, perPage);

  const queryArgs = useMemo(() => {
    const filterArgs = mapFiltersToArgs(filters);
    return { ...baseArgs, ...filterArgs, limit, offset } as unknown as TArgs;
  }, [baseArgs, mapFiltersToArgs, filters, limit, offset]);

  const options = queryOptions?.(queryArgs);
  const { data, isFetching, isError, error } = useQueryHook(queryArgs, options);

  // ---- Update pagination state after fetch --------------------------------

  useEffect(() => {
    if (isFetching) return;
    if (data === undefined) return;
    // Only update when current page advances or matches the frontier
    if (page < paginationState.maxLoadedPage) return;

    const receivedCount = data.length;
    const hasMore = receivedCount >= limit && receivedCount > 0;

    setPaginationState((prev) => ({
      maxLoadedPage: Math.max(prev.maxLoadedPage, page),
      hasMorePage: hasMore,
    }));
  }, [isFetching, data, page, limit, paginationState.maxLoadedPage]);

  // ---- Derived values -----------------------------------------------------

  const { maxLoadedPage, hasMorePage } = paginationState;
  const totalPages = maxLoadedPage + (hasMorePage ? 1 : 0);
  const items: TData[] = data ?? [];

  // ---- Callbacks ----------------------------------------------------------

  const setPage = useCallback(
    (newPage: number) => {
      setParams({ page: newPage } as any);
    },
    [setParams],
  );

  const setFilters = useCallback(
    (updates: Partial<FilterValues<F>>) => {
      setParams({ ...updates, page: 1 } as any);
    },
    [setParams],
  );

  const setPerPage = useCallback(
    (newPerPage: number) => {
      setStorage({ perPage: newPerPage } as any);
      setParams({ page: 1 } as any);
    },
    [setStorage, setParams],
  );

  // ---- Return -------------------------------------------------------------

  return {
    items,
    isLoading: isFetching,
    isError,
    error: error ?? null,
    page,
    limit: perPage,
    maxLoadedPage,
    hasMorePage,
    totalPages,
    filters,
    setPage,
    setFilters,
    setPerPage,
  };
}
